import { useState, useEffect } from "react";

// ─── DATA ────────────────────────────────────────────────────────────────────

const expertise = [
  { code: "FedRAMP", label: "FedRAMP Authorization", desc: "ATO preparation, SSP development, continuous monitoring, and 3PAO coordination for cloud service providers seeking federal authorization." },
  { code: "NIST", label: "NIST 800-53 / RMF", desc: "Full Risk Management Framework implementation — categorization, control selection, implementation, assessment, and authorization packages." },
  { code: "CMMC", label: "CMMC & DoD Compliance", desc: "CMMC Level 2/3 readiness assessments, gap analysis, and remediation roadmaps for defense contractors handling CUI." },
  { code: "ITAR", label: "ITAR / EAR Audits", desc: "Export control compliance reviews, technology control plans, and audit preparation for organizations handling controlled technical data." },
  { code: "PKI", label: "PKI / HSM Architecture", desc: "nShield HSM deployment, certificate lifecycle management, and cryptographic policy design for federal and enterprise environments." },
  { code: "AI-GOV", label: "AI Governance / FedRAMP", desc: "Embedding AI/ML governance into NIST 800-53 frameworks — unified risk registers, AI system inventories, and AI-specific POA&Ms." },
];

const certBadges = ["CRISC", "AWS SAA", "nCSE (Entrust)", "CISSP (In Progress)"];

const projects = [
  {
    id: "moveit",
    label: "BREACH FIX",
    title: "MOVEit Secure Transfer Demo",
    desc: "Production-level remediation of the Fiserv/MOVEit breach pattern. Full AWS deployment with Terraform IaC, OPA policy gates, and GuardDuty monitoring.",
    controls: ["SI-2", "CM-3", "SC-7", "SI-4", "IR-6"],
    tools: ["AWS Transfer Family", "Terraform", "OPA", "GuardDuty", "KMS"],
    status: "In Progress",
    color: "#f87171",
  },
  {
    id: "oscal",
    label: "OSCAL",
    title: "FedRAMP Moderate SSP Template",
    desc: "OSCAL-native SSP for a fictional AWS GovCloud system. 15 NIST 800-53 Rev 5 controls with machine-readable YAML output ready for PMO submission.",
    controls: ["AC-2", "AU-2", "CM-6", "IA-2", "SC-7"],
    tools: ["OSCAL JSON/YAML", "VS Code", "GitHub"],
    status: "Planned",
    color: "#22d3ee",
  },
  {
    id: "ai-gov",
    label: "AI GOVERNANCE",
    title: "AI Governance Control Mapper",
    desc: "Airtable-based framework mapping AI/ML systems to NIST 800-53 controls. Includes AI System Inventory, unified risk register, and AI-specific POA&M tracker.",
    controls: ["CM-8", "RA-3", "SA-11", "SC-28", "SI-12"],
    tools: ["Airtable", "NIST AI RMF", "Python"],
    status: "Published",
    color: "#a78bfa",
  },
  {
    id: "conmon",
    label: "CONMON",
    title: "Continuous Monitoring Dashboard",
    desc: "Live ConMon dashboard tracking vulnerability findings, POA&M aging, control health, and monthly ConMon metrics — built on free tooling.",
    controls: ["CA-7", "RA-5", "SI-2", "AU-6"],
    tools: ["Google Looker Studio", "AWS Inspector", "Google Sheets"],
    status: "Planned",
    color: "#34d399",
  },
  {
    id: "paramify",
    label: "TOOL COMPARISON",
    title: "GRC Platform Comparison: Paramify vs Airtable vs ServiceNow",
    desc: "First practitioner comparison built on direct demo access. Solution capability model analysis, OSCAL output quality, ConMon automation depth, and FedRAMP 20x readiness.",
    controls: ["All SSP controls", "CA-7", "CM-3", "OSCAL"],
    tools: ["Paramify", "Airtable", "ServiceNow GRC"],
    status: "In Progress",
    color: "#fbbf24",
  },
  {
    id: "zta",
    label: "ARCHITECTURE",
    title: "Zero Trust Reference Architecture",
    desc: "FedRAMP-aligned Zero Trust architecture diagram mapping CISA ZT pillars to NIST 800-53 controls. Includes implementation roadmap for cloud-first federal environments.",
    controls: ["AC-4", "IA-2", "SC-7", "SC-8", "SI-4"],
    tools: ["draw.io", "NIST 800-207", "AWS"],
    status: "Planned",
    color: "#38bdf8",
  },
];

// ─── CASE STUDY: CLOUDRAMP ───────────────────────────────────────────────────
const cloudRampSections = [
  {
    id: "problem",
    label: "The Problem",
    content: `AI adoption inside federal contracting is accelerating — but governance is not keeping pace. Most organizations treat AI governance as a parallel initiative running alongside traditional security controls. This creates duplicate documentation, disconnected risk assessment, unclear ownership, and an inability to demonstrate control effectiveness to 3PAOs.\n\nAs Dr. Leonard Simon, CISSP put it: "AI didn't enter federal environments through governance. It entered through productivity. And in NIST and CMMC-aligned organizations, that order creates real exposure."`,
  },
  {
    id: "solution",
    label: "Embedded Approach",
    content: `CloudRamp Analytics demonstrates four core principles:\n\n1. Single Risk Register — AI risks alongside traditional cyber risks, prioritized together, not in silos.\n\n2. AI Systems Linked to Controls — every AI system directly references NIST 800-53 controls (SA-11, SC-28, AU-6) rather than living in a separate inventory.\n\n3. AI-Specific POA&Ms in the Same Tracker — remediation prioritized by risk severity, not by domain. 3PAO findings tracked in one system.\n\n4. Continuous Monitoring Includes AI Metrics — AI drift and security drift monitored together. Single source of truth for FedRAMP PMO submissions.`,
  },
  {
    id: "architecture",
    label: "Architecture",
    content: `Platform: Airtable (scales to ServiceNow/Archer)\n\nTable 1 — Controls Register: 15 NIST 800-53 Rev 5 High baseline controls with implementation type, 3PAO status, automation level, and linked risks/POA&Ms.\n\nTable 2 — Risk Register: 12 unified risks (6 traditional + 6 AI/ML). Inherent risk scored as Likelihood × Impact. AI risks include model poisoning (R-007, score 15), prompt injection (R-008, score 20), and AI change control gaps (R-009, score 16).\n\nTable 3 — POA&M Tracker: 8 POA&Ms (3 traditional + 5 AI). Auto-calculated aging. 3PAO impact tagged per item.\n\nTable 4 — AI System Inventory: 5 systems tracked with model type, CUI processing flags, human oversight requirements, explainability methods, and direct control/risk linkages.`,
  },
  {
    id: "insights",
    label: "Key Insights",
    content: `NIST 800-53 controls already cover AI — you don't need new frameworks, you need extensions:\n\n• Model poisoning → SA-11 (extend to adversarial input testing)\n• Prompt injection → SC-28 + AC-6 (input sanitization + LLM data access limits)\n• Training data leakage → AC-16 + SI-12 (CUI classification for ML datasets)\n• Supply chain risk → SA-4 + SR-5 (AI vendor security assessments)\n• Lack of explainability → AU-6 (model interpretability for audit defense)\n• AI change control → CM-3 (ML model versioning and approval gates)\n\nThis is the structured risk domain approach validated by Dr. Derek A. Smith (former federal leader): "This is impressive." — 691+ LinkedIn impressions in 48 hours.`,
  },
];

// ─── BREACH LAB: PAYPAL ──────────────────────────────────────────────────────
const paypalBreachSections = [
  {
    id: "what-happened",
    label: "What Happened",
    content: `Timeline: July – December 2025\nImpact: 100+ customers with SSNs exposed over a 6-month window\nRoot cause: "Software coding error" — a change was deployed to production without adequate testing, security review, or monitoring in place to detect the resulting data exposure.\n\nThe exposure persisted for six months before detection, which means monitoring controls failed independently of the initial coding error. This is a compounding failure across development, operations, and compliance functions simultaneously.`,
  },
  {
    id: "nist-failures",
    label: "NIST 800-53 Failures",
    content: `CM-3 (Change Management): Code change deployed without security impact analysis or change control board review. No rollback plan documented.\n\nSI-4 (System Monitoring): No alerting triggered during 6 months of abnormal data access patterns. GuardDuty or equivalent not tuned to detect PII exfiltration.\n\nSC-28 (Protection at Rest): SSNs stored without field-level encryption. Full exposure once query logic was broken.\n\nAU-6 (Audit Review): Audit logs existed but were not reviewed. No automated correlation rules to surface anomalous access.\n\nAC-2 (Account Management): Overly permissive access — the coding error could access data beyond what that service account should have been able to reach.`,
  },
  {
    id: "iso-failures",
    label: "ISO 27001 Failures",
    content: `A.12.1.2 (Change Management): No documented change management procedure enforced at code deployment. Change not risk-assessed before go-live.\n\nA.12.4.1/3 (Event Logging): Logging was present but event correlation and alerting were absent. Logs without review are not a control — they are an audit artifact.\n\nA.10.1.1 (Cryptographic Controls): No cryptographic protection applied to SSN fields specifically. Encryption policy either did not exist or was not enforced at the field level.\n\nA.9.2.5 (Access Rights Review): Service account permissions were never reviewed after initial provisioning. Least privilege principle not enforced.`,
  },
  {
    id: "what-should",
    label: "What Should Have Been in Place",
    content: `Preventive (before breach): Mandatory security review gate in CI/CD pipeline. Terraform policy-as-code enforcing field encryption for PII. Change control board sign-off for data-layer changes. Static code analysis scanning for unprotected PII queries.\n\nDetective (during breach): CloudWatch/Splunk alert on anomalous SSN query volume. DLP solution monitoring data egress. Automated AU-6 correlation rules with PagerDuty escalation. Weekly access log review against baseline.\n\nCorrective (after detection): Documented incident response playbook. Mandatory 72-hour breach notification procedure. Post-incident POA&M with root cause and control gaps documented. Tabletop exercise to validate IR procedures.`,
  },
  {
    id: "poam",
    label: "The POA&M That Never Existed",
    content: `POAM-001: CM-3 — No change management procedure for data-layer deployments\nSeverity: High | Target: 30 days | Owner: DevOps Lead\nRemediation: Implement mandatory security review gate in CI/CD. All data-layer PRs require security team approval.\n\nPOAM-002: SC-28 — SSN fields stored without field-level encryption\nSeverity: Critical | Target: 15 days | Owner: Security Architect\nRemediation: Deploy AWS KMS field encryption for all PII columns. Verify with automated compliance scan.\n\nPOAM-003: AU-6 — Audit logs not correlated or reviewed\nSeverity: High | Target: 21 days | Owner: Security Operations\nRemediation: Deploy Splunk correlation searches for PII access anomalies. PagerDuty integration for threshold alerts.\n\nPOAM-004: SI-4 — No behavioral monitoring for data exfiltration patterns\nSeverity: High | Target: 30 days | Owner: DevOps\nRemediation: Enable GuardDuty findings → SNS → PagerDuty. Tune for SSN access volume thresholds.`,
  },
  {
    id: "lessons",
    label: "FedRAMP / ATO Lessons",
    content: `For FedRAMP environments, this breach pattern would have blocked or revoked an ATO:\n\n• A 3PAO testing CM-3 would find no evidence of change control procedures — immediate POA&M, likely ATO delay.\n• AU-6 failure with no evidence of log review = automatic finding. FedRAMP ConMon requires monthly log review evidence.\n• SC-28 gap on PII fields = High finding. FedRAMP High baseline requires encryption at rest for all sensitive data categories.\n• The 6-month detection gap would trigger a Significant Change notification requirement and potential JAB review.\n\nBottom line: Every control that failed in this breach is tested by 3PAOs. They are not edge cases — they are foundational assessment criteria. The cost of prevention (estimated $50K–$200K for full implementation) is a fraction of breach costs ($3M–$10M+ in fines, remediation, and reputational damage).`,
  },
];

const trainingModules = [
  { id: "01", title: "FedRAMP From Zero", desc: "How cloud authorization actually works — from initial categorization through ATO.", level: "Foundational", duration: "4 hrs" },
  { id: "02", title: "Reading & Writing SSPs", desc: "System Security Plan deep dive. How to document controls that pass 3PAO review.", level: "Intermediate", duration: "6 hrs" },
  { id: "03", title: "NIST 800-53 Control Families", desc: "Practical walkthrough of all 20 control families — what they mean operationally.", level: "Intermediate", duration: "8 hrs" },
  { id: "04", title: "CMMC Level 2 Readiness", desc: "Gap assessment methodology, evidence collection, and remediation roadmaps.", level: "Intermediate", duration: "5 hrs" },
  { id: "05", title: "Continuous Monitoring Programs", desc: "Building a ConMon program that satisfies FedRAMP requirements without overwhelming your team.", level: "Advanced", duration: "4 hrs" },
  { id: "06", title: "POA&M Management", desc: "How to write, track, and close Plan of Action & Milestones that agencies actually accept.", level: "Foundational", duration: "3 hrs" },
];

const levelColor = {
  Foundational: { color: "#34d399", bg: "rgba(52,211,153,0.08)" },
  Intermediate: { color: "#38bdf8", bg: "rgba(56,189,248,0.08)" },
  Advanced: { color: "#a78bfa", bg: "rgba(167,139,250,0.08)" },
};

// ─── STYLES ──────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Syne:wght@700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #26314f; }
  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: #26314f; }
  ::-webkit-scrollbar-thumb { background: #22d3ee; border-radius: 2px; }

  .grid-bg {
    background-image:
      linear-gradient(rgba(34,211,238,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(34,211,238,0.025) 1px, transparent 1px);
    background-size: 48px 48px;
  }
  .noise { position: relative; }
  .noise::before {
    content: '';
    position: absolute; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none; z-index: 0;
  }

  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes glow-pulse {
    0%,100% { box-shadow: 0 0 20px rgba(34,211,238,0.15); }
    50% { box-shadow: 0 0 40px rgba(34,211,238,0.3); }
  }

  .cursor-blink { animation: blink 1.2s step-end infinite; }
  .slide-up { animation: slideUp 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
  .fade-in { animation: fadeIn 0.5s ease forwards; }

  .nav-link {
    color: rgba(226,232,240,0.70);
    text-decoration: none;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.14em;
    transition: color 0.2s;
    padding-bottom: 2px;
    border-bottom: 1px solid transparent;
  }
  .nav-link:hover { color: #22d3ee; border-bottom-color: rgba(34,211,238,0.5); }

  .card {
    border: 1px solid rgba(255,255,255,0.055);
    border-radius: 14px;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.25s, background 0.25s, transform 0.25s;
  }
  .card:hover {
    border-color: rgba(34,211,238,0.3);
    background: rgba(34,211,238,0.035);
    transform: translateY(-2px);
  }

  .tab-btn {
    cursor: pointer;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    border-radius: 6px;
    border: 1px solid transparent;
    transition: all 0.2s;
    background: transparent;
    color: rgba(226,232,240,0.78);
    font-family: 'IBM Plex Mono', monospace;
    text-align: left;
    width: 100%;
  }
  .tab-btn:hover { color: rgba(226,232,240,0.8); background: rgba(255,255,255,0.03); }
  .tab-btn.active {
    color: #22d3ee;
    background: rgba(34,211,238,0.08);
    border-color: rgba(34,211,238,0.2);
  }

  .cta-primary {
    display: inline-block;
    background: #22d3ee;
    color: #26314f;
    padding: 13px 28px;
    border-radius: 7px;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-decoration: none;
    transition: all 0.2s;
    font-family: 'IBM Plex Mono', monospace;
  }
  .cta-primary:hover { background: #67e8f9; transform: translateY(-1px); box-shadow: 0 8px 28px rgba(34,211,238,0.28); }

  .cta-ghost {
    display: inline-block;
    border: 1px solid rgba(34,211,238,0.35);
    color: #22d3ee;
    padding: 13px 28px;
    border-radius: 7px;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-decoration: none;
    transition: all 0.2s;
    font-family: 'IBM Plex Mono', monospace;
  }
  .cta-ghost:hover { background: rgba(34,211,238,0.08); }

  .control-tag {
    display: inline-block;
    padding: 3px 10px;
    background: rgba(34,211,238,0.08);
    border: 1px solid rgba(34,211,238,0.2);
    border-radius: 4px;
    font-size: 11px;
    color: #22d3ee;
    font-weight: 600;
    letter-spacing: 0.08em;
    margin: 2px;
  }

  .section-label {
    font-size: 10px;
    color: #22d3ee;
    letter-spacing: 0.22em;
    font-weight: 700;
    margin-bottom: 12px;
  }
  .section-h2 {
    font-family: 'Syne', sans-serif;
    font-size: clamp(28px, 3.5vw, 44px);
    font-weight: 800;
    color: #f0f6ff;
    line-height: 1.1;
  }

  .insight-stat {
    border: 1px solid rgba(34,211,238,0.15);
    border-radius: 10px;
    padding: 20px 24px;
    background: rgba(34,211,238,0.03);
  }

  .divider { border: none; border-top: 1px solid rgba(255,255,255,0.05); margin: 0; }

  pre { white-space: pre-wrap; word-break: break-word; }

  .post-content p, .post-content pre {
    font-size: 13.5px;
    color: rgba(226,232,240,0.80);
    line-height: 1.95;
    margin-bottom: 16px;
    font-family: 'IBM Plex Mono', monospace;
  }

  /* ── MOBILE MENU ── */
  .hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    cursor: pointer;
    padding: 6px;
    background: none;
    border: none;
  }
  .hamburger span {
    display: block;
    width: 22px;
    height: 2px;
    background: rgba(226,232,240,0.80);
    border-radius: 2px;
    transition: all 0.3s;
  }
  .hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
  .hamburger.open span:nth-child(2) { opacity: 0; }
  .hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

  .mobile-menu {
    display: none;
    position: fixed;
    top: 62px;
    left: 0; right: 0;
    background: rgba(22, 28, 45, 0.98);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(34,211,238,0.1);
    padding: 20px 28px 28px;
    z-index: 99;
    flex-direction: column;
    gap: 4px;
  }
  .mobile-menu.open { display: flex; }
  .mobile-menu a {
    color: rgba(226,232,240,0.78);
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.14em;
    padding: 13px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: color 0.2s;
  }
  .mobile-menu a:hover { color: #22d3ee; }
  .mobile-menu a:last-child { border-bottom: none; }

  /* ── RESPONSIVE ── */
  @media (max-width: 768px) {
    .nav-desktop { display: none !important; }
    .hamburger { display: flex !important; }

    .hero-stats {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    .hero-ctas {
      flex-direction: column !important;
      align-items: flex-start !important;
    }
    .hero-ctas a {
      width: 100%;
      text-align: center;
    }

    .tab-layout {
      flex-direction: column !important;
    }
    .tab-sidebar {
      flex-direction: row !important;
      flex-wrap: wrap !important;
      gap: 6px !important;
      border-right: none !important;
      border-bottom: 1px solid rgba(255,255,255,0.07) !important;
      padding-right: 0 !important;
      padding-bottom: 14px !important;
      margin-bottom: 14px !important;
    }
    .tab-sidebar button {
      width: auto !important;
      flex: 1 1 auto !important;
      min-width: 120px !important;
      text-align: center !important;
    }

    .portfolio-grid {
      grid-template-columns: 1fr 1fr !important;
    }

    .projects-grid {
      grid-template-columns: 1fr !important;
    }

    .section-split {
      grid-template-columns: 1fr !important;
    }

    .expertise-grid {
      grid-template-columns: 1fr !important;
    }

    .training-grid {
      grid-template-columns: 1fr !important;
    }

    .footer-inner {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
    }
  }

  @media (max-width: 480px) {
    .hero-stats {
      grid-template-columns: 1fr !important;
    }
    .portfolio-grid {
      grid-template-columns: 1fr !important;
    }
  }
`;

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export default function GRCSecurityControls() {
  const [scrolled, setScrolled] = useState(false);
  const [cloudTab, setCloudTab] = useState("problem");
  const [paypalTab, setPaypalTab] = useState("what-happened");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const closeMobile = () => setMobileMenuOpen(false);

  const W = { maxWidth: 1160, margin: "0 auto", padding: "0 28px" };
  const FONT = { fontFamily: "'IBM Plex Mono', monospace" };

  const navLinks = ["Expertise", "Case Studies", "Projects", "Breach Lab", "Contact"];

  return (
    <div style={{ ...FONT, background: "#26314f", color: "#e2e8f0", minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        borderBottom: scrolled ? "1px solid rgba(34,211,238,0.08)" : "1px solid transparent",
        background: scrolled ? "rgba(22,28,45,0.96)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        transition: "all 0.3s",
      }}>
        <div style={{ ...W, display: "flex", alignItems: "center", justifyContent: "space-between", height: 62 }}>
          <a href="#home" style={{ textDecoration: "none" }}>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 900, letterSpacing: "0.04em" }}>
              <span style={{ color: "#22d3ee" }}>GRC</span>
              <span style={{ color: "#f0f6ff" }}>SecurityControls</span>
              <span style={{ color: "#22d3ee" }} className="cursor-blink">_</span>
            </div>
            <div style={{ fontSize: 9, color: "rgba(226,232,240,0.55)", letterSpacing: "0.2em", marginTop: 1 }}>
              FEDERAL COMPLIANCE · RISK · GOVERNANCE
            </div>
          </a>

          {/* Desktop nav */}
          <div className="nav-desktop" style={{ display: "flex", gap: 28, alignItems: "center" }}>
            {navLinks.map(l => (
              <a key={l} href={`#${l.toLowerCase().replace(" ", "-")}`} className="nav-link">{l.toUpperCase()}</a>
            ))}
            <a href="#contact" className="cta-primary" style={{ padding: "8px 18px", fontSize: 10 }}>ENGAGE →</a>
          </div>

          {/* Hamburger */}
          <button
            className={`hamburger${mobileMenuOpen ? " open" : ""}`}
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`mobile-menu${mobileMenuOpen ? " open" : ""}`}>
        {navLinks.map(l => (
          <a key={l} href={`#${l.toLowerCase().replace(" ", "-")}`} onClick={closeMobile}>
            {l.toUpperCase()}
          </a>
        ))}
        <a href="#contact" onClick={closeMobile} style={{ color: "#22d3ee", fontWeight: 700, borderBottom: "none", marginTop: 8 }}>
          ENGAGE →
        </a>
      </div>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section id="home" className="grid-bg noise" style={{ paddingTop: 130, paddingBottom: 90, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "15%", right: "8%", width: 480, height: 480, borderRadius: "50%", background: "radial-gradient(circle, rgba(34,211,238,0.055) 0%, transparent 68%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "5%", left: "2%", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 68%)", pointerEvents: "none" }} />

        <div style={W}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", border: "1px solid rgba(34,211,238,0.2)", borderRadius: 4, marginBottom: 36, background: "rgba(34,211,238,0.04)" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22d3ee", animation: "blink 2s step-end infinite" }} />
            <span style={{ fontSize: 10, color: "#22d3ee", letterSpacing: "0.2em", fontWeight: 700 }}>AVAILABLE FOR GRC ENGAGEMENTS · 2026</span>
          </div>

          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(38px, 5.5vw, 72px)", fontWeight: 900, lineHeight: 1.0, marginBottom: 24, letterSpacing: "-0.02em", color: "#f0f6ff" }}>
            Federal Compliance<br />
            <span style={{ color: "#22d3ee", textShadow: "0 0 40px rgba(34,211,238,0.35)" }}>From the Inside.</span>
          </h1>

          <p style={{ fontSize: 16, color: "rgba(226,232,240,0.75)", maxWidth: 560, lineHeight: 1.85, marginBottom: 14 }}>
            7+ years building FedRAMP authorizations, NIST 800-53 control programs, CMMC readiness efforts, AI governance structures, and DoD-aligned compliance operations.
          </p>
          <p style={{ fontSize: 12, color: "rgba(226,232,240,0.65)", maxWidth: 500, lineHeight: 1.75, marginBottom: 44 }}>
            Former Entrust FedRAMP Certification Manager & HSM Product Security Architect.<br />CRISC · AWS SAA · nCSE certified · CISSP candidate.
          </p>

          <div className="hero-ctas" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 52 }}>
            <a href="#contact" className="cta-primary">REQUEST CONSULTATION</a>
            <a href="#case-studies" className="cta-ghost">VIEW CASE STUDIES</a>
            <a href="#breach-lab" className="cta-ghost" style={{ borderColor: "rgba(248,113,113,0.35)", color: "#f87171" }}>BREACH LAB →</a>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 52 }}>
            {certBadges.map(b => (
              <div key={b} style={{ padding: "5px 13px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 4, fontSize: 10, color: "rgba(226,232,240,0.70)", letterSpacing: "0.1em", background: "rgba(255,255,255,0.045)" }}>{b}</div>
            ))}
          </div>

          {/* Stats — 2x2 on mobile via class */}
          <div className="hero-stats" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, maxWidth: 680 }}>
            {[["7+", "Years GRC"], ["Multiple", "ATO Programs Supported"], ["FedRAMP High", "Experience"], ["NIST · CMMC · ITAR", "Frameworks"]].map(([val, label]) => (
              <div key={label} className="insight-stat">
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: "#22d3ee", marginBottom: 4 }}>{val}</div>
                <div style={{ fontSize: 10, color: "rgba(226,232,240,0.60)", letterSpacing: "0.1em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ── FEATURED CASE STUDY ──────────────────────────────────────────── */}
      <section id="case-studies" style={{ padding: "88px 28px", background: "rgba(34,211,238,0.018)" }}>
        <div style={W}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 20, marginBottom: 48 }}>
            <div>
              <div className="section-label">{"// FEATURED CASE STUDY"}</div>
              <h2 className="section-h2">Building AI Governance<br />into FedRAMP High</h2>
              <p style={{ fontSize: 13, color: "rgba(226,232,240,0.78)", marginTop: 10 }}>CloudRamp Analytics GRC Program · Published Feb 27, 2026</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ padding: "10px 18px", border: "1px solid rgba(34,211,238,0.2)", borderRadius: 8, fontSize: 11, color: "#22d3ee", background: "rgba(34,211,238,0.04)" }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>DR. DEREK A. SMITH</div>
                <div style={{ color: "rgba(226,232,240,0.70)", fontSize: 10 }}>"This is impressive" — Former Federal Leader</div>
              </div>
              <div style={{ padding: "10px 18px", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, fontSize: 11, color: "rgba(226,232,240,0.70)" }}>
                691+ LinkedIn impressions · 48 hours
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 36 }}>
            {["NIST 800-53 Rev 5 Controls", "12 Risks (6 AI/ML)", "8 POA&Ms (5 AI)", "5 AI Systems", "Airtable Architecture"].map(t => (
              <span key={t} className="control-tag">{t}</span>
            ))}
          </div>

          {/* Tabbed case study */}
          <div className="tab-layout" style={{ display: "flex", gap: 32 }}>
            <div className="tab-sidebar" style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180, borderRight: "1px solid rgba(255,255,255,0.07)", paddingRight: 24 }}>
              {cloudRampSections.map(s => (
                <button key={s.id} className={`tab-btn${cloudTab === s.id ? " active" : ""}`} onClick={() => setCloudTab(s.id)}>
                  {s.label}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, background: "rgba(0,0,0,0.15)", borderRadius: 10, padding: "24px 28px" }}>
              <div className="post-content">
                {cloudRampSections.find(s => s.id === cloudTab)?.content.split("\n").map((line, i) => (
                  <p key={i}>{line || "\u00a0"}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ── BREACH LAB ───────────────────────────────────────────────────── */}
      <section id="breach-lab" style={{ padding: "88px 28px" }}>
        <div style={W}>
          <div style={{ marginBottom: 12 }}>
            <div className="section-label" style={{ color: "#f87171" }}>{"// BREACH ANALYSIS LAB"}</div>
            <h2 className="section-h2">When Controls Fail</h2>
            <p style={{ fontSize: 13, color: "rgba(226,232,240,0.70)", marginTop: 10, maxWidth: 640 }}>
              Real breach. Real control failures. Real remediation. Each analysis maps public incidents to NIST 800-53 and ISO 27001 control failures, then engineers the fix.
            </p>
          </div>

          {/* Disclaimer */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 4, marginBottom: 40, background: "rgba(248,113,113,0.04)" }}>
            <span style={{ fontSize: 10, color: "rgba(248,113,113,0.8)", letterSpacing: "0.14em" }}>
              PUBLIC BREACH ANALYSIS — Based on publicly reported information and independent control interpretation. No insider knowledge.
            </span>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 36 }}>
            {["PayPal Data Breach", "July–Dec 2025", "100+ SSNs Exposed", "CM-3 · SI-4 · SC-28 · AU-6 · AC-2"].map(t => (
              <span key={t} className="control-tag" style={{ borderColor: "rgba(248,113,113,0.25)", color: "#f87171", background: "rgba(248,113,113,0.05)" }}>{t}</span>
            ))}
          </div>

          <div className="tab-layout" style={{ display: "flex", gap: 32 }}>
            <div className="tab-sidebar" style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200, borderRight: "1px solid rgba(255,255,255,0.07)", paddingRight: 24 }}>
              {paypalBreachSections.map(s => (
                <button key={s.id} className={`tab-btn${paypalTab === s.id ? " active" : ""}`} onClick={() => setPaypalTab(s.id)} style={paypalTab === s.id ? { color: "#f87171", background: "rgba(248,113,113,0.07)", borderColor: "rgba(248,113,113,0.2)" } : {}}>
                  {s.label}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, background: "rgba(0,0,0,0.15)", borderRadius: 10, padding: "24px 28px" }}>
              <div className="post-content">
                {paypalBreachSections.find(s => s.id === paypalTab)?.content.split("\n").map((line, i) => (
                  <p key={i}>{line || "\u00a0"}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ── EXPERTISE ────────────────────────────────────────────────────── */}
      <section id="expertise" style={{ padding: "88px 28px", background: "rgba(34,211,238,0.018)" }}>
        <div style={W}>
          <div style={{ marginBottom: 52 }}>
            <div className="section-label">{"// CORE COMPETENCIES"}</div>
            <h2 className="section-h2">Compliance Domains</h2>
          </div>
          <div className="expertise-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
            {expertise.map(e => (
              <div key={e.code} className="card" style={{ padding: "26px 28px" }}>
                <div style={{ fontSize: 10, color: "#22d3ee", letterSpacing: "0.2em", fontWeight: 700, marginBottom: 10 }}>{e.code}</div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, fontWeight: 700, color: "#f0f6ff", marginBottom: 10 }}>{e.label}</div>
                <p style={{ fontSize: 12.5, color: "rgba(226,232,240,0.72)", lineHeight: 1.85 }}>{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ── PROJECTS ─────────────────────────────────────────────────────── */}
      <section id="projects" style={{ padding: "88px 28px" }}>
        <div style={W}>
          <div style={{ marginBottom: 52 }}>
            <div className="section-label">{"// ENGINEERING PROOF"}</div>
            <h2 className="section-h2">Portfolio Projects</h2>
            <p style={{ fontSize: 13, color: "rgba(226,232,240,0.70)", marginTop: 12, maxWidth: 600 }}>
              Reference builds that turn compliance controls into architectures, evidence, and operational workflows — not just documentation.
            </p>
          </div>

          <div className="projects-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 18 }}>
            {projects.map(p => (
              <div key={p.id} className="card" style={{ padding: "26px 28px", borderTop: `2px solid ${p.color}22` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <span style={{ fontSize: 10, color: p.color, letterSpacing: "0.18em", fontWeight: 700, padding: "2px 8px", background: `${p.color}12`, borderRadius: 4, border: `1px solid ${p.color}25` }}>
                    {p.label}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 4,
                    color: p.status === "Published" ? "#34d399" : p.status === "In Progress" ? "#fbbf24" : "rgba(226,232,240,0.45)",
                    background: p.status === "Published" ? "rgba(52,211,153,0.08)" : p.status === "In Progress" ? "rgba(251,191,36,0.08)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${p.status === "Published" ? "rgba(52,211,153,0.2)" : p.status === "In Progress" ? "rgba(251,191,36,0.2)" : "rgba(255,255,255,0.06)"}`,
                  }}>
                    {p.status}
                  </span>
                </div>

                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, color: "#f0f6ff", marginBottom: 10, lineHeight: 1.3 }}>
                  {p.title}
                </div>
                <p style={{ fontSize: 12, color: "rgba(226,232,240,0.68)", lineHeight: 1.8, marginBottom: 18 }}>{p.desc}</p>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: "rgba(226,232,240,0.40)", letterSpacing: "0.14em", marginBottom: 7 }}>CONTROLS</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {p.controls.map(c => <span key={c} className="control-tag" style={{ fontSize: 10 }}>{c}</span>)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10, color: "rgba(226,232,240,0.40)", letterSpacing: "0.14em", marginBottom: 7 }}>TOOLS</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {p.tools.map(t => (
                      <span key={t} style={{ fontSize: 10, padding: "2px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, color: "rgba(226,232,240,0.60)" }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ── TRAINING ─────────────────────────────────────────────────────── */}
      <section id="training" style={{ padding: "88px 28px", background: "rgba(34,211,238,0.018)" }}>
        <div style={W}>
          <div style={{ marginBottom: 48 }}>
            <div className="section-label">{"// KNOWLEDGE TRANSFER"}</div>
            <h2 className="section-h2">Team Enablement</h2>
            <p style={{ fontSize: 13, color: "rgba(226,232,240,0.70)", marginTop: 10, maxWidth: 560 }}>
              Practitioner-built curriculum for teams standing up GRC programs, preparing for FedRAMP authorization, or building NIST expertise.
            </p>
          </div>
          <div className="training-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 14 }}>
            {trainingModules.map(m => {
              const lc = levelColor[m.level];
              return (
                <div key={m.id} className="card" style={{ padding: "22px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                    <span style={{ fontSize: 11, color: "rgba(226,232,240,0.25)", fontWeight: 700 }}>{m.id}</span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 600, color: lc.color, background: lc.bg }}>{m.level}</span>
                      <span style={{ fontSize: 10, color: "rgba(226,232,240,0.60)" }}>{m.duration}</span>
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 700, color: "#f0f6ff", marginBottom: 8 }}>{m.title}</div>
                  <p style={{ fontSize: 12, color: "rgba(226,232,240,0.72)", lineHeight: 1.8 }}>{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ── CONTACT ──────────────────────────────────────────────────────── */}
      <section id="contact" style={{ padding: "88px 28px" }}>
        <div style={{ ...W, maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <div className="section-label" style={{ textAlign: "center" }}>{"// ENGAGE"}</div>
          <h2 className="section-h2" style={{ textAlign: "center", marginBottom: 16 }}>Let's Work Together</h2>
          <p style={{ fontSize: 14, color: "rgba(226,232,240,0.70)", lineHeight: 1.9, marginBottom: 44 }}>
            Available for FedRAMP consulting, GRC program buildouts, CMMC readiness, AI governance integration, and training engagements. Contract and full-time considered.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 36 }}>
            <a href="mailto:victor@grcsecuritycontrols.com" className="cta-primary">EMAIL DIRECTLY</a>
            <a href="https://www.linkedin.com/in/victor-adeleke-214083177/" target="_blank" rel="noreferrer" className="cta-ghost">LINKEDIN PROFILE</a>
          </div>
          <div style={{ fontSize: 11, color: "rgba(226,232,240,0.60)", letterSpacing: "0.1em" }}>
            victor@grcsecuritycontrols.com · San Francisco Bay Area
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "28px" }}>
        <div className="footer-inner" style={{ ...W, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 800 }}>
            <span style={{ color: "#22d3ee" }}>GRC</span><span style={{ color: "#f0f6ff" }}>SecurityControls</span>
          </div>
          <div style={{ fontSize: 10, color: "rgba(226,232,240,0.55)", letterSpacing: "0.1em" }}>
            © 2026 GRCSecurityControls.com · Victor Adeleke · Federal Compliance · Risk · Governance
          </div>
          <a href="https://grcsecuritycontrols.com/blog" target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "#22d3ee", letterSpacing: "0.1em", textDecoration: "none", fontWeight: 600 }}>
            BLOG / INSIGHTS →
          </a>
        </div>
      </footer>
    </div>
  );
}
